import { useCallback, useEffect, useState } from 'react';
import { type ApiClientStatus, ThingsApiClient } from '@lifepowr/components/src/utils/api-client';

export type OAuthIntegrationResponse = {
  /** The type of authentication (e.g., "oauth2_code"). */
  type?: string;

  /** Brand ID of the integration method. */
  brand?: string;

  /** Human-readable label for the integration. */
  label?: string;

  /** List of supported asset types for this brand. */
  assets?: Array<string>;

  /** The URL to initiate authentication (e.g., OAuth 2.0 authorization URL). */
  uri?: string;
};

export type IntegrationListResponse = Array<OAuthIntegrationResponse>;

function createRedirectToPortalUrl(includePath: boolean, thingName: string | null): string {
  const { protocol, host, pathname } = window.location;
  let redirectionUrl = `${protocol}//${host}`;

  if (includePath) {
    redirectionUrl += pathname;
  }

  // It makes no difference to the backend to use the 'noname' as the thingId,
  // as we won't be using the returned `redirect_uri` value if we don't supply a valid thingId.
  // We need it for the cases where we only care getting the list of all possible integrations.
  redirectionUrl +=
    '&state=' +
    encodeURIComponent(
      JSON.stringify({
        thingId: thingName || 'noname',
      }),
    );

  return redirectionUrl;
}

/**
 * IntegrationsListApiClient is responsible for fetching the list of available integrations for cloudflex user and
 * ensuring that only one API call is made per account, essentially debouncing multiple calls to it.
 *
 * It uses an observer pattern to notify registered listeners about the status of the API call.
 */
class IntegrationsListApiClient extends ThingsApiClient<IntegrationListResponse> {
  // biome-ignore lint/complexity/noUselessConstructor: We need to keep the default constructor here, as the parent constructor is not public
  constructor() {
    super();
  }

  protected async preApiInvocationAction(): Promise<any> {
    return {};
  }

  protected async postApiInvocationAction(_: IntegrationListResponse): Promise<void> {}

  protected async apiInvocationErrorAction(error: any): Promise<void> {
    console.log(error);
  }

  protected getApiUri(): string {
    // /cloudflex/integrations/list is the current CloudFlex API (ECS); the legacy
    // /integrations/list service returns an outdated authorize URL whose
    // redirect_uri shape breaks Enphase's site binding.
    return `/cloudflex/integrations/list?redirect_uri=${createRedirectToPortalUrl(true, this.thingName)}`;
  }
}

const integrationsListApiClient = new IntegrationsListApiClient();

export type IntegrationsApiHookType = {
  integrationsListApiClientStatus: ApiClientStatus<IntegrationListResponse>;
  triggerIntegrationsList: () => Promise<ApiClientStatus<IntegrationListResponse>>;
  fetchIntegrationsList: (thingName: string) => Promise<ApiClientStatus<IntegrationListResponse>>;
};

export function useIntegrationsApiHook(): IntegrationsApiHookType {
  const [integrationsListApiClientStatus, setIntegrationsListApiClientStatus] = useState(integrationsListApiClient.getStatus());

  useEffect(() => {
    // Returns the unsubscribe function to stop listening to the integrations API client status changes when the hook is unmounted
    return integrationsListApiClient.subscribe((newIntegrationsApiClientStatus) => {
      setIntegrationsListApiClientStatus({ ...newIntegrationsApiClientStatus });
    });
  }, []);

  const triggerIntegrationsList = useCallback(async (): Promise<ApiClientStatus<IntegrationListResponse>> => {
    integrationsListApiClient.resetState();

    await integrationsListApiClient.forceFetchData().catch((e: any) => {
      console.error('Error fetching integrations list:', e);
    });

    return integrationsListApiClient.getStatus();
  }, []);

  const fetchIntegrationsList = useCallback(async (thingName: string): Promise<ApiClientStatus<IntegrationListResponse>> => {
    await integrationsListApiClient.fetchData(thingName).catch((e: any) => {
      console.error('Error fetching integrations list:', e);
    });

    return integrationsListApiClient.getStatus();
  }, []);

  return {
    integrationsListApiClientStatus,
    triggerIntegrationsList,
    fetchIntegrationsList,
  };
}
